/**
 * Names a Mermaid diagram from its source, for the expand/collapse control on
 * {@link MermaidCollapsible}.
 *
 * A message that answers with three diagrams stacks three identical "Expand
 * diagram" buttons; reading the diagram keyword off the source turns those into
 * "Expand flowchart", "Expand pie chart", "Expand state diagram", which is the
 * difference between a scannable transcript and three anonymous boxes.
 *
 * Kept pure (no React) so it can be unit tested against real diagram sources —
 * see mermaid-diagram-label.spec.ts.
 */

/**
 * Mermaid's diagram keywords mapped to what a person would call them.
 *
 * Keys are matched after the `-v2` / `-beta` suffix is stripped, so
 * `stateDiagram-v2`, `xychart-beta` and friends resolve without a second entry
 * each. Anything unrecognized — a keyword newer than this list — falls back to
 * the generic label rather than showing a raw identifier like `packet-beta`.
 */
const DIAGRAM_LABELS: Record<string, string> = {
  architecture: 'architecture diagram',
  block: 'block diagram',
  c4context: 'C4 diagram',
  classdiagram: 'class diagram',
  erdiagram: 'ER diagram',
  flowchart: 'flowchart',
  gantt: 'Gantt chart',
  gitgraph: 'git graph',
  graph: 'flowchart',
  journey: 'user journey',
  kanban: 'kanban board',
  mindmap: 'mind map',
  packet: 'packet diagram',
  pie: 'pie chart',
  quadrantchart: 'quadrant chart',
  radar: 'radar chart',
  requirementdiagram: 'requirement diagram',
  sankey: 'sankey diagram',
  sequencediagram: 'sequence diagram',
  statediagram: 'state diagram',
  timeline: 'timeline',
  treemap: 'treemap',
  xychart: 'XY chart',
  zenuml: 'ZenUML diagram',
};

/** What an unrecognized or unreadable diagram is called. */
export const GENERIC_DIAGRAM_LABEL = 'diagram';

/**
 * The first line that actually declares the diagram type, skipping everything
 * Mermaid allows to precede it: a `---`-fenced YAML frontmatter block, `%%`
 * comments, `%%{init: …}%%` directives, and blank lines.
 */
function declarationLine(source: string): string | undefined {
  const lines = source.split('\n');
  let index = 0;

  // Frontmatter, when present, must be the very first non-blank content.
  while (index < lines.length && lines[index]?.trim() === '') {
    index += 1;
  }
  if (lines[index]?.trim() === '---') {
    index += 1;
    while (index < lines.length && lines[index]?.trim() !== '---') {
      index += 1;
    }
    // Step past the closing fence; if it was never found we're at the end
    // anyway and the loop below returns undefined.
    index += 1;
  }

  for (; index < lines.length; index += 1) {
    const line = lines[index]?.trim() ?? '';
    if (line !== '' && !line.startsWith('%%')) {
      return line;
    }
  }
  return undefined;
}

/**
 * The human-readable name of the diagram `source` declares — `'flowchart'`,
 * `'sequence diagram'`, `'pie chart'` — or {@link GENERIC_DIAGRAM_LABEL} when
 * the source is empty, still streaming, or uses a keyword this list predates.
 *
 * Lowercase, so it reads correctly mid-sentence ("Expand flowchart"); the two
 * initialisms that must stay uppercase are spelled that way in the table.
 */
export function mermaidDiagramLabel(source: string): string {
  const declaration = declarationLine(source);
  if (declaration === undefined) {
    return GENERIC_DIAGRAM_LABEL;
  }

  // The keyword runs up to the first space or, for `graph TD`-style headers
  // written without one, the first `-`-suffixed variant marker.
  const keyword = (declaration.split(/[\s:]/)[0] ?? '')
    .toLowerCase()
    .replace(/-(v\d+|beta)$/, '');

  return DIAGRAM_LABELS[keyword] ?? GENERIC_DIAGRAM_LABEL;
}
